import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';

import server from '../../../api/server';
import { setOrganisation } from '../../../actions';
import AdminShell from '../layout/AdminShell';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import DocumentTypeModal from '../settings/DocumentTypeModal';
import { PlusIcon, UploadIcon, PencilIcon, MenuIcon } from '../ui/icons';

// Redesign of newadmin/Docs_Settings.js — same react-sortable-hoc reorder
// posting /doctype/edit + setOrganisation. Rows are responsive divs instead
// of a table so drag & drop works the same on mobile.
const EMPTY_DOCUMENT = {
    label: '',
    min_documents: 1,
    description: '',
    category: '',
};

const SortableItem = SortableElement(({ file, onEdit }) => (
    <li className="flex items-center gap-3 px-4 sm:px-6 py-4 bg-white select-none">
        <span className="text-[#9CA3AF] cursor-grab flex-shrink-0" title="Drag to reorder">
            <MenuIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-bold text-[#1F2937]">{file.label}</p>
                {file.category ? <Badge tone="accent">{file.category}</Badge> : null}
                <Badge tone="neutral">Min {file.min_documents}</Badge>
            </div>
            {file.description ? (
                <p className="text-xs text-[#6B7280] mt-1 truncate">{file.description}</p>
            ) : null}
        </div>
        <Button variant="secondary" className="!px-3 !py-2" onClick={onEdit} title="Edit document type">
            <PencilIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Edit</span>
        </Button>
    </li>
));

const SortableList = SortableContainer(({ items, onEdit }) => (
    <ul className="divide-y divide-[#F0F1F5]">
        {items.map((file, index) => (
            <SortableItem
                key={`item-${file.label}-${index}`}
                index={index}
                file={file}
                onEdit={() => onEdit(file, index)}
            />
        ))}
    </ul>
));

const DocsSettingsPage = () => {
    const organisation = useSelector((s) => s.organisation);
    const dispatch = useDispatch();

    const [modalOpen, setModalOpen] = useState(false);
    const [edit, setEdit] = useState(false);
    const [index, setIndex] = useState(0);
    const [activeDocument, setActiveDocument] = useState({ ...EMPTY_DOCUMENT });

    const files = organisation?.file_upload_settings?.files || [];

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    // Port of Docs_Settings.onSortEnd — same /doctype/edit payload.
    const onSortEnd = async ({ oldIndex, newIndex }) => {
        const docSettings = [...organisation.file_upload_settings.files];
        const result = reorder(docSettings, oldIndex, newIndex);
        const updated = { ...organisation };
        updated.file_upload_settings.files = result;
        await server.post('/doctype/edit', {
            organisation_id: organisation.id,
            organisation: updated,
        });
        dispatch(setOrganisation(updated));
    };

    const openEdit = (file, fileIndex) => {
        setIndex(fileIndex);
        setActiveDocument(file);
        setEdit(true);
        setModalOpen(true);
    };

    const openNew = () => {
        setActiveDocument({ ...EMPTY_DOCUMENT });
        setEdit(false);
        setModalOpen(true);
    };

    return (
        <AdminShell title="Document Collection Settings">
            <DocumentTypeModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                edit={edit}
                index={index}
                activeDocument={activeDocument}
            />

            <Card
                title="Document Types"
                subtitle="Add, reorder or remove frequently requested document types"
                actions={
                    <Button onClick={openNew}>
                        <PlusIcon className="h-4 w-4" />
                        New Document Type
                    </Button>
                }
                bodyClassName="p-0"
            >
                {files.length === 0 ? (
                    <EmptyState
                        icon={<UploadIcon className="h-6 w-6" />}
                        title="No document types yet"
                        subtitle="Create a document type to request it from clients."
                        action={
                            <Button onClick={openNew}>
                                <PlusIcon className="h-4 w-4" />
                                New Document Type
                            </Button>
                        }
                    />
                ) : (
                    <SortableList items={files} onEdit={openEdit} onSortEnd={onSortEnd} distance={4} lockAxis="y" />
                )}
            </Card>
        </AdminShell>
    );
};

export default DocsSettingsPage;
